// ./src/components/features/Inventory/PurchaseOrdersFilterBar/PurchaseOrdersFilterBar.tsx
//
// Filter controls for the Purchase Orders list: by lifecycle status. Fully
// controlled — the page owns the value and maps it to a PurchaseOrderFilter. Renders
// inline on desktop and inside the mobile FilterDrawer (vertical). See em-scaffold-ui.

import React from "react";
import { Group, Box, Stack, Button, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { PURCHASE_ORDER_STATUS_VALUES, PURCHASE_ORDER_STATUS_LABELS } from "@/utils/api";
import { AppIcon, SelectField } from "@/components/generic";

export interface PurchaseOrdersFilterValue {
  poStatus: string | null;
}

export interface PurchaseOrdersFilterBarProps {
  value: PurchaseOrdersFilterValue;
  onChange: (value: PurchaseOrdersFilterValue) => void;
  disabled?: boolean;
  showClear?: boolean;
  vertical?: boolean;
}

export const EMPTY_PURCHASE_ORDERS_FILTER: PurchaseOrdersFilterValue = {
  poStatus: null,
};

const isFilterActive = (value: PurchaseOrdersFilterValue): boolean => Boolean(value.poStatus);

const STATUS_STYLE: React.CSSProperties = { flex: "1 1 220px", minWidth: 200 };

const PurchaseOrdersFilterBarComponent: React.FC<PurchaseOrdersFilterBarProps> = ({
  value,
  onChange,
  disabled = false,
  showClear = true,
  vertical = false,
}) => {
  const { t } = useTranslation(["inventory", "common"]);

  const active = isFilterActive(value);

  const statusData = PURCHASE_ORDER_STATUS_VALUES.map((v) => ({ value: v, label: PURCHASE_ORDER_STATUS_LABELS[v] || v }));

  const statusControl = (
    <Box style={vertical ? undefined : STATUS_STYLE}>
      <SelectField
        label={t("inventory:po.filters.status.label", { defaultValue: "Status" })}
        placeholder={t("inventory:po.filters.status.placeholder", { defaultValue: "All statuses" })}
        data={statusData}
        value={value.poStatus}
        onChange={(_n, v) => onChange({ ...value, poStatus: v || null })}
        clearable
        disabled={disabled}
        size="md"
      />
    </Box>
  );

  if (vertical) {
    return <Stack gap="md">{statusControl}</Stack>;
  }

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      {statusControl}

      {active && showClear && (
        <Box>
          <Text size="md" fw={500} mb={4} aria-hidden style={{ visibility: "hidden" }}>
            &nbsp;
          </Text>
          <Button variant="subtle" color="gray" size="md" onClick={() => onChange(EMPTY_PURCHASE_ORDERS_FILTER)} disabled={disabled} leftSection={<AppIcon iconName="X" size={16} />}>
            {t("common:buttons.clear", { defaultValue: "Clear" })}
          </Button>
        </Box>
      )}
    </Group>
  );
};

export const PurchaseOrdersFilterBar = React.memo(PurchaseOrdersFilterBarComponent);
